export const API_URL = process.env.REACT_APP_GRAPHQL_URL ?? 'http://localhost:4000/graphql';
import { connectivityManager } from '../network/connectivity';

const DEFAULT_GRAPHQL_TIMEOUT_MS = 15_000;

export function buildBackendUrl(pathname: string) {
  const graphqlUrl = new URL(API_URL, typeof window !== 'undefined' ? window.location.origin : 'http://localhost:3000');
  return new URL(pathname, graphqlUrl).toString();
}

export class GraphQLRequestError extends Error {
  code?: string;
  details?: Record<string, unknown>;

  constructor(message: string, code?: string, details?: Record<string, unknown>) {
    super(message);
    this.name = 'GraphQLRequestError';
    this.code = code;
    this.details = details;
  }
}

export async function graphqlRequest<TData>(
  query: string,
  variables?: Record<string, unknown>,
  options?: {
    keepalive?: boolean;
  },
): Promise<TData> {
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timeoutId =
    controller && typeof window !== 'undefined'
      ? window.setTimeout(() => controller.abort(), DEFAULT_GRAPHQL_TIMEOUT_MS)
      : null;

  let response: Response;
  try {
    response = await fetch(API_URL, {
      method: 'POST',
      keepalive: options?.keepalive,
      signal: controller?.signal,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ query, variables }),
    });
  } catch (error) {
    void connectivityManager.markUnreachable();
    if (error instanceof Error && error.name === 'AbortError') {
      throw new GraphQLRequestError('The request timed out while waiting for the backend.', 'TIMEOUT');
    }
    throw new GraphQLRequestError('Unable to reach the backend.');
  } finally {
    if (timeoutId !== null) {
      window.clearTimeout(timeoutId);
    }
  }

  void connectivityManager.markReachable();

  if (!response.ok) {
    const code =
      response.status === 401
        ? 'UNAUTHENTICATED'
        : response.status === 403
          ? 'HTTP_403'
          : `HTTP_${response.status}`;
    throw new GraphQLRequestError(`Request failed with status ${response.status}`, code, { status: response.status });
  }

  const body = (await response.json()) as {
    data?: TData;
    errors?: Array<{ message: string; extensions?: Record<string, unknown> & { code?: string } }>;
  };

  if (body.errors?.length) {
    const first = body.errors[0];
    throw new GraphQLRequestError(first.message, first.extensions?.code, first.extensions);
  }

  if (!body.data) {
    throw new GraphQLRequestError('Missing response payload');
  }

  return body.data;
}
